import { useState } from 'react'
import { messageOf } from '../lib/errors'
import type { PageWarning } from './admin/WarningsFlag'
import { adminLabel, type EndorsementAdminRow, type EndorsementSave } from '../lib/endorsementAdmin'

/**
 * #/admin/stars/endorsements. 128; People and roles' treatment 9 Oct.
 *
 * Presentational, fed by props, in components/ — so the suite can render it
 * (an effect does not run under renderToString, and a route file pulls in an
 * SVG the test bundler cannot resolve).
 *
 * ── WHAT THE SCREEN IS FOR ──────────────────────────────────────────────────
 *
 * Nobody waits on this screen. People endorse for anything and the give form
 * nudges them towards what exists; this is where the words get TIDIED
 * afterwards. Four acts, each the server's to refuse:
 *   * make a word official — it keeps its id, so every recognition already
 *     carrying it shows the new label and translation;
 *   * feature it — a preset chip on the give form;
 *   * hide it — never suggested again, still shown where it was used;
 *   * merge a duplicate into another word — an alias, so Unmerge undoes it.
 *
 * ── A TABLE AND A TRAY (David, 9 Oct) ───────────────────────────────────────
 *
 * It was a card per word, each with an Edit button that opened a form inline
 * and pushed the rest of the list down. Now one line per word — what it is,
 * whether it is a chip or hidden, its Spanish, its uses — and the four acts in
 * the right-hand tray for one word at a time (`EndorsementEditor`).
 *
 * ── NO PEOPLE, AND NO RANKING ───────────────────────────────────────────────
 *
 * Uses per word only. No "who coined it", no "who has the most" (D10, B22).
 * The table keeps the server's order and has no sort control on Uses.
 */
export function EndorsementTable({
  rows,
  selectedId,
  onSelect,
}: {
  rows: EndorsementAdminRow[]
  selectedId: string | null
  onSelect: (id: string) => void
}) {
  return (
    <div className="roles__tablewrap">
      <table className="admin__table people__table starsadmin__table">
        <thead>
          <tr>
            <th scope="col">Endorsement</th>
            <th scope="col">Kind</th>
            <th scope="col">Spanish</th>
            <th scope="col" className="starsadmin__num">Uses</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const open = selectedId === r.id
            return (
              <tr
                key={r.id}
                data-endorsement={r.slug}
                className={'people__row' + (open ? ' is-selected' : '') + (r.isHidden ? ' is-suspended' : '')}
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest('button, a, input, label')) return
                  onSelect(r.id)
                }}
              >
                <th scope="row" className="roles__person">
                  <span className="roles__who">
                    <button
                      type="button"
                      className="roles__open"
                      aria-haspopup="dialog"
                      aria-expanded={open}
                      onClick={() => onSelect(r.id)}
                    >
                      <span className="roles__name">{adminLabel(r)}</span>
                    </button>
                    {r.aliases.length > 0 && (
                      <span className="roles__meta">
                        Merged into this: {r.aliases.map((a) => a.label).join(', ')}
                      </span>
                    )}
                  </span>
                </th>
                <td>
                  <span className="starsadmin__tags">
                    <span className={`rolechip${r.isOfficial ? ' rolechip--priv' : ''}`}>
                      {r.isOfficial ? 'Official' : 'Community'}
                    </span>
                    {r.isFeatured && <span className="rolechip">Featured</span>}
                    {r.isHidden && <span className="rolechip">Hidden</span>}
                  </span>
                </td>
                <td>
                  {!r.isOfficial ? (
                    <span className="starsadmin__none">—</span>
                  ) : r.labelEs ? (
                    r.labelEs
                  ) : (
                    <span className="roles__meta">No Spanish yet</span>
                  )}
                </td>
                <td className="starsadmin__num">{r.uses.toLocaleString()}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** The tray's body for one word. Always the form: the tray IS the edit. */
export function EndorsementEditor({
  row,
  others,
  onSave,
  onMerge,
  onUnmerge,
}: {
  row: EndorsementAdminRow
  /** Every other canonical word — the merge targets. */
  others: EndorsementAdminRow[]
  onSave: (input: EndorsementSave) => Promise<void>
  onMerge: (fromId: string, intoId: string) => Promise<void>
  onUnmerge: (id: string) => Promise<void>
}) {
  const [official, setOfficial] = useState(row.isOfficial)
  const [featured, setFeatured] = useState(row.isFeatured)
  const [hidden, setHidden] = useState(row.isHidden)
  const [order, setOrder] = useState(String(row.sortOrder))
  const [en, setEn] = useState(row.labelEn ?? row.label)
  const [es, setEs] = useState(row.labelEs ?? '')
  const [into, setInto] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const orderN = /^-?\d+$/.test(order.trim()) ? Number(order.trim()) : null
  const invalid = orderN === null || (official && en.trim().length < 2)
  const dirty =
    official !== row.isOfficial ||
    (official && featured) !== row.isFeatured ||
    hidden !== row.isHidden ||
    orderN !== row.sortOrder ||
    (official && en.trim() !== (row.labelEn ?? row.label)) ||
    (official && es.trim() !== (row.labelEs ?? ''))
  const idBase = `endorse-${row.id}`

  async function run(fn: () => Promise<void>) {
    setBusy(true)
    setError(null)
    setSaved(false)
    try {
      await fn()
      setSaved(true)
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  function save() {
    if (invalid || orderN === null) return
    return run(() =>
      onSave({
        id: row.id,
        isOfficial: official,
        isFeatured: official && featured,
        isHidden: hidden,
        sortOrder: orderN,
        labelEn: official ? en.trim() : null,
        labelEs: official && es.trim() ? es.trim() : null,
      }),
    )
  }

  function merge() {
    const target = others.find((o) => o.id === into)
    if (!target) return
    // Irreversible-looking, so it confirms — though Unmerge does undo it.
    if (!window.confirm(`Merge “${adminLabel(row)}” into “${adminLabel(target)}”? Every recognition carrying it will show “${adminLabel(target)}”. Unmerge undoes this.`)) return
    return run(() => onMerge(row.id, target.id))
  }

  return (
    <div className="panel panel--tray starsadmin__editor" data-endorsement={row.slug}>
      <div className="tray__meta">
        <span className={`rolechip${row.isOfficial ? ' rolechip--priv' : ''}`}>
          {row.isOfficial ? 'Official' : 'Community'}
        </span>
        {row.isFeatured && <span className="rolechip">Featured</span>}
        {row.isHidden && <span className="rolechip">Hidden</span>}
        <span className="earnrules__meta">
          {row.uses === 1 ? 'Used once' : `Used ${row.uses.toLocaleString()} times`}
        </span>
      </div>

      <section className="panel__section">
        <h3 className="panel__h3">How it shows</h3>
        <div className="starsadmin__checks">
          <label className="earnrules__toggle" htmlFor={`${idBase}-off`}>
            <input
              id={`${idBase}-off`}
              type="checkbox"
              checked={official}
              onChange={(e) => {
                setOfficial(e.target.checked)
                if (!e.target.checked) setFeatured(false)
              }}
            />
            <span>Official</span>
          </label>
          <label className="earnrules__toggle" htmlFor={`${idBase}-feat`}>
            <input
              id={`${idBase}-feat`}
              type="checkbox"
              checked={official && featured}
              disabled={!official}
              onChange={(e) => setFeatured(e.target.checked)}
            />
            <span>Featured chip on the give form</span>
          </label>
          <label className="earnrules__toggle" htmlFor={`${idBase}-hid`}>
            <input
              id={`${idBase}-hid`}
              type="checkbox"
              checked={hidden}
              onChange={(e) => setHidden(e.target.checked)}
            />
            <span>Hidden from suggestions</span>
          </label>
        </div>
        <div className="earnrules__fields">
          {official && (
            <>
              <label className="stars__field" htmlFor={`${idBase}-en`}>
                <span className="earnrules__label">English</span>
                <input id={`${idBase}-en`} type="text" maxLength={40} value={en} onChange={(e) => setEn(e.target.value)} />
              </label>
              <label className="stars__field" htmlFor={`${idBase}-es`}>
                <span className="earnrules__label">Spanish (optional)</span>
                <input id={`${idBase}-es`} type="text" maxLength={40} value={es} onChange={(e) => setEs(e.target.value)} />
              </label>
            </>
          )}
          <label className="stars__field starsadmin__order" htmlFor={`${idBase}-order`}>
            <span className="earnrules__label">Chip order</span>
            <input
              id={`${idBase}-order`}
              type="number"
              inputMode="numeric"
              step={1}
              value={order}
              onChange={(e) => setOrder(e.target.value)}
            />
          </label>
        </div>
        {!official && (
          <p className="earnrules__meta">
            A community word shows exactly as it was first typed (“{row.label}”). Make it official to
            give it a proper label and a translation; an official word keeps its id, so every
            recognition already carrying it shows the new label.
          </p>
        )}
        {invalid && (
          <p className="earnrules__warn" role="alert">
            {orderN === null ? 'Chip order must be a whole number.' : 'An official endorsement needs an English label.'}
          </p>
        )}
        <div className="starsadmin__save">
          <button type="button" className="btn btn--primary btn--sm" disabled={!dirty || invalid || busy} onClick={save}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          {saved && !dirty && <span className="earnrules__meta" role="status">Saved.</span>}
        </div>
      </section>

      {row.aliases.length > 0 && (
        <section className="panel__section">
          <h3 className="panel__h3">Merged into this</h3>
          <ul className="starsadmin__aliases">
            {row.aliases.map((a) => (
              <li key={a.id}>
                <span>
                  {a.label} <span className="earnrules__meta">({a.uses.toLocaleString()})</span>
                </span>
                <button
                  type="button"
                  className="btn btn--quiet btn--xs"
                  disabled={busy}
                  onClick={() => run(() => onUnmerge(a.id))}
                >
                  Unmerge
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="panel__section">
        <h3 className="panel__h3">A duplicate?</h3>
        <p className="earnrules__meta">
          Merge it into the word it means, and every recognition carrying it shows that word
          instead. Unmerge puts it back.
        </p>
        <div className="earnrules__fields">
          <label className="stars__field endorseadmin__into" htmlFor={`${idBase}-into`}>
            <span className="earnrules__label">Merge into</span>
            <select id={`${idBase}-into`} value={into} onChange={(e) => setInto(e.target.value)}>
              <option value="">Choose the word this means…</option>
              {others.map((o) => (
                <option key={o.id} value={o.id}>
                  {adminLabel(o)}{o.isOfficial ? ' (official)' : ''}
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="btn" disabled={!into || busy} onClick={merge}>
            Merge
          </button>
        </div>
      </section>

      {error && <p className="earnrules__warn" role="alert">{error}</p>}
    </div>
  )
}

/** The endorsements tab's notices, for the flag. */
export function endorsementNotices(rows: EndorsementAdminRow[]): PageWarning[] {
  const out: PageWarning[] = []
  const noSpanish = rows.filter((r) => r.isOfficial && !r.labelEs).length
  if (noSpanish > 0) {
    out.push({
      id: 'no-spanish',
      tone: 'info',
      text: (
        <>
          {noSpanish.toLocaleString()} official {noSpanish === 1 ? 'word has' : 'words have'} no Spanish
          yet, so Spanish readers see the English. Filter by <strong>Spanish › No Spanish yet</strong> to
          find {noSpanish === 1 ? 'it' : 'them'}.
        </>
      ),
    })
  }
  out.push({
    id: 'how',
    tone: 'info',
    text: (
      <>
        People say what a recognition is for in their own words, and the give form suggests the words
        that already exist as they type. <strong>Featured</strong> official words are the preset chips
        on the give form.
      </>
    ),
  })
  return out
}
